import { useCallback, useEffect, useState } from 'react';
import { useNotify } from 'react-admin';
import {
  Alert,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { dateTimeNoSeconds } from '../../shared/dateTime';
import { toErrorMessage } from '../userOffers/orderActionsApi';
import { ReasonDialog } from './AssignmentParts';
import {
  PERIOD_LABELS,
  UNIT_LABELS,
  createValue,
  dictionaryLabel,
  fetchValues,
  plDay,
  withdrawValue,
  type FuelSurchargeType,
  type FuelSurchargeValue,
} from './fuelSurchargesApi';

/**
 * Wartości korekty w okresach (B4). Źródło wartości przyjdzie od CS — do
 * tego czasu lista jest pusta, a zlecenie mówi „brak — czeka na wartości od
 * CS” (nigdy zero). Wartości się nie poprawia: błędną wycofuje się
 * z powodem i wpisuje nową. Okresy jednego rodzaju się nie nakładają.
 */

const DATE_SLOT = { inputLabel: { shrink: true } } as const;

type Form = { validFrom: string; validTo: string; unit: string; value: string; currency: string; source: string; note: string };
const EMPTY: Form = { validFrom: '', validTo: '', unit: 'percent', value: '', currency: '', source: '', note: '' };

export const ValuesTab = ({ types, canEdit }: { types: FuelSurchargeType[]; canEdit: boolean }) => {
  const notify = useNotify();
  const [typeId, setTypeId] = useState<number | ''>('');
  const [rows, setRows] = useState<FuelSurchargeValue[]>([]);
  const [form, setForm] = useState<Form | null>(null);
  const [withdrawing, setWithdrawing] = useState<FuelSurchargeValue | null>(null);
  const [busy, setBusy] = useState(false);
  const type = types.find((t) => t.id === typeId) ?? null;

  const load = useCallback(async () => {
    if (typeId === '') {
      setRows([]);
      return;
    }
    try {
      setRows(await fetchValues(typeId));
    } catch (e) {
      notify(toErrorMessage(e), { type: 'error' });
    }
  }, [typeId, notify]);

  useEffect(() => {
    void load();
  }, [load]);

  const canSave =
    !!form &&
    form.validFrom !== '' &&
    form.validTo !== '' &&
    form.value.trim() !== '' &&
    form.source.trim() !== '' &&
    (form.unit === 'percent' || form.currency.trim() !== '');

  const save = async () => {
    if (!form || typeId === '') return;
    setBusy(true);
    try {
      const out = await createValue(typeId, {
        validFrom: form.validFrom,
        validTo: form.validTo,
        value: form.value.trim(),
        unit: form.unit,
        currency: form.unit === 'amount' ? form.currency.trim() : undefined,
        source: form.source.trim(),
        note: form.note.trim() || undefined,
      });
      notify(out.periodNote ? `Zapisano. ${out.periodNote}` : 'Zapisano wartość', {
        type: out.periodNote ? 'warning' : 'success',
      });
      setForm(null);
      void load();
    } catch (e) {
      notify(toErrorMessage(e), { type: 'error' });
    } finally {
      setBusy(false);
    }
  };

  const set = (patch: Partial<Form>) => form && setForm({ ...form, ...patch });

  return (
    <>
      <Stack direction="row" spacing={1} sx={{ alignItems: 'center', mb: 2 }}>
        <TextField
          select
          label="Rodzaj korekty"
          value={typeId}
          onChange={(e) => setTypeId(Number(e.target.value))}
          sx={{ minWidth: 360 }}
        >
          {types.map((t) => (
            <MenuItem key={t.id} value={t.id}>
              {t.name}
            </MenuItem>
          ))}
        </TextField>
        {type ? (
          <Typography variant="body2" color="text.secondary">
            okres: {dictionaryLabel(PERIOD_LABELS, type.period)}
          </Typography>
        ) : null}
        {canEdit && type ? (
          <Button variant="outlined" onClick={() => setForm(EMPTY)} sx={{ ml: 'auto' }}>
            Dodaj wartość
          </Button>
        ) : null}
      </Stack>
      {type === null ? (
        <Typography variant="body2" color="text.secondary">
          Wybierz rodzaj korekty, żeby zobaczyć jego wartości.
        </Typography>
      ) : rows.length === 0 ? (
        <Alert severity="info" data-testid="fuel-values-empty">
          Brak wartości dla tego rodzaju — przyjdą od CS. Do tego czasu na zleceniu: „brak — czeka na wartości
          od CS”, nigdy zero.
        </Alert>
      ) : (
        <Table size="small" data-testid="fuel-values">
          <TableHead>
            <TableRow>
              <TableCell>Okres</TableCell>
              <TableCell>Wartość</TableCell>
              <TableCell>Źródło</TableCell>
              <TableCell>Dodał</TableCell>
              {canEdit && <TableCell />}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((v) => (
              <TableRow key={v.id} data-testid="fuel-value-row" sx={v.withdrawnAt ? { opacity: 0.6 } : undefined}>
                <TableCell>
                  {plDay(v.validFrom)} – {plDay(v.validTo)}
                  {v.periodNote ? (
                    <Typography variant="caption" color="warning.main" component="div">
                      {v.periodNote}
                    </Typography>
                  ) : null}
                </TableCell>
                <TableCell>
                  {v.text}
                  {v.withdrawnAt ? (
                    <Chip size="small" variant="outlined" label="wycofana" sx={{ ml: 1 }} title={v.withdrawnReason ?? undefined} />
                  ) : null}
                </TableCell>
                <TableCell sx={{ wordBreak: 'break-word' }}>
                  {v.source}
                  {v.note ? (
                    <Typography variant="caption" color="text.secondary" component="div">
                      {v.note}
                    </Typography>
                  ) : null}
                </TableCell>
                <TableCell>
                  {v.createdByName ?? '—'}
                  <Typography variant="caption" color="text.secondary" component="div">
                    {dateTimeNoSeconds(v.createdAt)}
                  </Typography>
                </TableCell>
                {canEdit && (
                  <TableCell align="right">
                    {v.withdrawnAt ? null : (
                      <Button size="small" color="warning" onClick={() => setWithdrawing(v)}>
                        Wycofaj
                      </Button>
                    )}
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      <Dialog open={form !== null} onClose={() => setForm(null)} fullWidth maxWidth="sm">
        <DialogTitle>Dodaj wartość: {type?.name}</DialogTitle>
        <DialogContent>
          {form && (
            <Stack spacing={2} sx={{ mt: 1 }}>
              <Stack direction="row" spacing={1}>
                <TextField
                  type="date"
                  label="Od"
                  required
                  value={form.validFrom}
                  onChange={(e) => set({ validFrom: e.target.value })}
                  slotProps={{ ...DATE_SLOT, htmlInput: { 'data-testid': 'fuel-value-from' } }}
                />
                <TextField
                  type="date"
                  label="Do"
                  required
                  value={form.validTo}
                  onChange={(e) => set({ validTo: e.target.value })}
                  slotProps={{ ...DATE_SLOT, htmlInput: { 'data-testid': 'fuel-value-to' } }}
                />
              </Stack>
              <Stack direction="row" spacing={1}>
                <TextField select label="Jednostka" value={form.unit} onChange={(e) => set({ unit: e.target.value })} sx={{ minWidth: 140 }}>
                  {Object.entries(UNIT_LABELS).map(([id, name]) => (
                    <MenuItem key={id} value={id}>
                      {name}
                    </MenuItem>
                  ))}
                </TextField>
                <TextField
                  label={form.unit === 'percent' ? 'Wartość (%)' : 'Kwota'}
                  required
                  value={form.value}
                  onChange={(e) => set({ value: e.target.value })}
                  slotProps={{ htmlInput: { inputMode: 'decimal', 'data-testid': 'fuel-value-value' } }}
                />
                {form.unit === 'amount' && (
                  <TextField
                    label="Waluta"
                    required
                    value={form.currency}
                    onChange={(e) => set({ currency: e.target.value.toUpperCase() })}
                    slotProps={{ htmlInput: { maxLength: 3 } }}
                    sx={{ width: 100 }}
                  />
                )}
              </Stack>
              <TextField
                label="Źródło wartości (np. plik CS z dnia…)"
                required
                value={form.source}
                onChange={(e) => set({ source: e.target.value })}
                slotProps={{ htmlInput: { maxLength: 300, 'data-testid': 'fuel-value-source' } }}
              />
              <TextField label="Uwagi" value={form.note} onChange={(e) => set({ note: e.target.value })} slotProps={{ htmlInput: { maxLength: 500 } }} />
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setForm(null)}>Anuluj</Button>
          <Button variant="contained" disabled={!canSave || busy} onClick={() => void save()}>
            Zapisz wartość
          </Button>
        </DialogActions>
      </Dialog>

      <ReasonDialog
        open={withdrawing !== null}
        title="Wycofaj wartość"
        text="Wycofana wartość zostaje w historii, ale przestaje obowiązywać. Poprawną wpisz jako nową wartość."
        confirmLabel="Wycofaj"
        onClose={() => setWithdrawing(null)}
        onConfirm={async (reason) => {
          if (!withdrawing) return;
          try {
            await withdrawValue(withdrawing.id, reason);
            notify('Wartość wycofana', { type: 'success' });
            setWithdrawing(null);
            void load();
          } catch (e) {
            notify(toErrorMessage(e), { type: 'error' });
          }
        }}
      />
    </>
  );
};
